import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import { Badge } from '../ui/badge';
import { 
  FeatherPlus,
  FeatherMessageSquare,
  FeatherPhone,
  FeatherMail,
  FeatherUsers,
  FeatherMoreHorizontal,
  FeatherCalendar,
  FeatherUser
} from '../ui/icons';
import { IconButton } from '../ui/IconButton';
import api from '../../services/api';
import './CommunicationLogs.css';

const CommunicationLogs = ({ companyId, onRefresh }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    communication_type: 'call',
    subject: '',
    content: '',
    outcome: '',
    next_action: ''
  });

  useEffect(() => {
    if (companyId) {
      fetchLogs();
    }
  }, [companyId]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/partner-companies/${companyId}`);
      setLogs(response.data.communication_logs || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch communication logs');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await api.post(`/api/partner-companies/${companyId}/communication`, formData);
      
      // Reset form
      setFormData({
        communication_type: 'call',
        subject: '',
        content: '',
        outcome: '',
        next_action: ''
      });
      
      setShowForm(false);
      
      // Refresh logs
      await fetchLogs();
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      setError('Failed to add communication log');
      console.error('Error adding log:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getCommunicationIcon = (type) => {
    const icons = {
      'call': <FeatherPhone />,
      'email': <FeatherMail />,
      'meeting': <FeatherUsers />,
      'other': <FeatherMessageSquare />
    };
    return icons[type] || <FeatherMessageSquare />;
  };

  const getCommunicationColor = (type) => {
    const colors = {
      'call': 'success',
      'email': 'info',
      'meeting': 'warning',
      'other': 'neutral'
    };
    return colors[type] || 'neutral';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const communicationTypes = [
    { value: 'call', label: 'Phone Call' },
    { value: 'email', label: 'Email' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'other', label: 'Other' }
  ];

  if (loading) {
    return (
      <div className="communication-logs">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading communication logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="communication-logs">
      {/* Header */}
      <div className="section-header">
        <div className="header-left">
          <h3>Communication Logs</h3>
          <Badge variant="neutral-tertiary">{logs.length} entries</Badge>
        </div>
        <Button
          variant="brand-primary"
          icon={<FeatherPlus />}
          onClick={() => setShowForm(true)}
        >
          Add Log
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <Button
            variant="neutral-tertiary"
            size="sm"
            onClick={() => setError('')}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Add Log Form */}
      {showForm && (
        <div className="add-log-form">
          <h4>Add Communication Log</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Type</label>
                <select
                  value={formData.communication_type}
                  onChange={(e) => handleInputChange('communication_type', e.target.value)}
                  className="form-select"
                  required
                >
                  {communicationTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Subject</label>
                <TextField
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  placeholder="Brief subject or topic"
                />
              </div>

              <div className="form-group full-width">
                <label>Content</label>
                <TextArea
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  placeholder="Detailed description of the communication"
                  rows={4}
                  required
                />
              </div>

              <div className="form-group">
                <label>Outcome</label>
                <TextField
                  value={formData.outcome}
                  onChange={(e) => handleInputChange('outcome', e.target.value)}
                  placeholder="What was the result?"
                />
              </div>

              <div className="form-group">
                <label>Next Action</label>
                <TextField
                  value={formData.next_action}
                  onChange={(e) => handleInputChange('next_action', e.target.value)}
                  placeholder="What needs to be done next?"
                />
              </div>
            </div>

            <div className="form-actions">
              <Button
                type="button"
                variant="neutral-tertiary"
                onClick={() => setShowForm(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="brand-primary"
                disabled={submitting}
              >
                {submitting ? 'Adding...' : 'Add Log'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Logs List */}
      {logs.length === 0 ? (
        <div className="empty-state">
          <FeatherMessageSquare size={48} />
          <h4>No Communication Logs</h4>
          <p>Start tracking your communications with this partner company</p>
          <Button
            variant="brand-primary"
            icon={<FeatherPlus />}
            onClick={() => setShowForm(true)}
          >
            Add First Log
          </Button>
        </div>
      ) : (
        <div className="logs-list">
          {logs.map(log => (
            <div key={log.id} className="log-item">
              <div className="log-header">
                <div className="log-type">
                  <IconButton
                    variant={getCommunicationColor(log.communication_type)}
                    size="sm"
                    icon={getCommunicationIcon(log.communication_type)}
                  />
                  <div className="log-meta">
                    <span className="log-type-label">
                      {communicationTypes.find(t => t.value === log.communication_type)?.label}
                    </span>
                    <span className="log-date">
                      <FeatherCalendar size={12} />
                      {formatDate(log.created_at)}
                    </span>
                  </div>
                </div>
                
                {log.user && (
                  <div className="log-user">
                    <FeatherUser size={14} />
                    <span>{log.user.display_name}</span>
                  </div>
                )}
              </div>

              {log.subject && (
                <div className="log-subject">
                  <strong>{log.subject}</strong>
                </div>
              )}

              <div className="log-content">
                {log.content}
              </div>

              {(log.outcome || log.next_action) && (
                <div className="log-details">
                  {log.outcome && (
                    <div className="log-outcome">
                      <strong>Outcome:</strong> {log.outcome}
                    </div>
                  )}
                  {log.next_action && (
                    <div className="log-next-action">
                      <strong>Next Action:</strong> {log.next_action}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunicationLogs; 